import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Input, Select, Button, Space, Tag, Modal, message,
  Typography, Row, Col, Card, DatePicker, Popconfirm
} from 'antd';
import {
  SearchOutlined, DeleteOutlined, EyeOutlined, MailOutlined,
  UserOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usersApi, UserListParams } from 'api/users';
import styled from 'styled-components';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const SegmentChip = styled.div<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 20px;
  cursor: pointer;
  margin-right: 8px;
  margin-bottom: 8px;
  background: ${p => p.active ? '#1a7a4f' : '#f5f5f5'};
  color: ${p => p.active ? '#fff' : '#333'};
  font-size: 13px;
  transition: all 0.2s;
  &:hover { opacity: 0.8; }
  .count { font-weight: 600; margin-left: 6px; }
`;

const STATUS_COLORS: Record<string, string> = {
  free: 'default',
  professional: 'green',
  enterprise: 'blue',
  free_trial: 'gold',
};

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [segments, setSegments] = useState<any>(null);
  const [activeSegment, setActiveSegment] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: UserListParams = {
        page,
        limit: pageSize,
        sort: sortField,
        order: sortOrder,
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (dateRange) {
        params.created_after = dateRange[0].toISOString();
        params.created_before = dateRange[1].toISOString();
      }

      // Apply segment-specific filters
      if (activeSegment === 'active_7d') {
        params.active_since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      } else if (activeSegment === 'active_30d') {
        params.active_since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      } else if (activeSegment === 'never_active') {
        params.inactive_since = new Date(0).toISOString();
      }

      const res = await usersApi.list(params);
      setUsers(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      message.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter, sortField, sortOrder, dateRange, activeSegment]);

  const fetchSegments = async () => {
    try {
      const res = await usersApi.getSegments();
      setSegments(res.data);
    } catch (err) {
      console.error('Failed to fetch segments:', err);
    }
  };

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchSegments(); }, []);

  const handleDelete = async (id: string) => {
    try {
      await usersApi.delete(id);
      message.success('User deleted');
      fetchUsers();
      fetchSegments();
    } catch (err: any) {
      message.error(err.response?.data?.messages?.[0] || 'Delete failed');
    }
  };

  const handleBulkDelete = async () => {
    try {
      const res = await usersApi.bulkDelete(selectedRowKeys as string[]);
      message.success(res.data.messages?.[0] || 'Users deleted');
      setSelectedRowKeys([]);
      fetchUsers();
      fetchSegments();
    } catch (err: any) {
      message.error(err.response?.data?.messages?.[0] || 'Bulk delete failed');
    }
  };

  const handleSegmentClick = (segment: string) => {
    if (activeSegment === segment) {
      setActiveSegment(null);
      setStatusFilter(undefined);
    } else {
      setActiveSegment(segment);
      if (['free', 'paid', 'trial'].includes(segment)) {
        if (segment === 'paid') {
          setStatusFilter('professional');
        } else if (segment === 'trial') {
          setStatusFilter('free_trial');
        } else {
          setStatusFilter(segment);
        }
      } else {
        setStatusFilter(undefined);
      }
    }
    setPage(1);
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      render: (email: string, record: any) => (
        <a onClick={() => navigate(`/users/${record._id}`)}>{email}</a>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string) => name || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Collections',
      key: 'collections',
      render: (_: any, record: any) => record.collections?.total || 0,
      sorter: false,
    },
    {
      title: 'Sign-ins',
      dataIndex: 'sign_in_count',
      key: 'sign_in_count',
      sorter: true,
    },
    {
      title: 'Last Active',
      dataIndex: 'last_activity_date',
      key: 'last_activity_date',
      sorter: true,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : 'Never',
    },
    {
      title: 'Joined',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />}
            onClick={() => navigate(`/users/${record._id}`)} />
          <Popconfirm
            title="Delete this user?"
            description="This will cascade-delete all their data."
            onConfirm={() => handleDelete(record._id)}
            okText="Delete"
            okType="danger"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
    if (sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Users</Title>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchUsers(); fetchSegments(); }}>
            Refresh
          </Button>
        </Col>
      </Row>

      {/* Segment chips */}
      {segments && (
        <div style={{ marginBottom: 16 }}>
          <SegmentChip active={activeSegment === 'free'} onClick={() => handleSegmentClick('free')}>
            Free <span className="count">{segments.free}</span>
          </SegmentChip>
          <SegmentChip active={activeSegment === 'paid'} onClick={() => handleSegmentClick('paid')}>
            Paid <span className="count">{segments.paid}</span>
          </SegmentChip>
          <SegmentChip active={activeSegment === 'trial'} onClick={() => handleSegmentClick('trial')}>
            Trial <span className="count">{segments.trial}</span>
          </SegmentChip>
          <SegmentChip active={activeSegment === 'active_7d'} onClick={() => handleSegmentClick('active_7d')}>
            Active 7d <span className="count">{segments.active_7d}</span>
          </SegmentChip>
          <SegmentChip active={activeSegment === 'active_30d'} onClick={() => handleSegmentClick('active_30d')}>
            Active 30d <span className="count">{segments.active_30d}</span>
          </SegmentChip>
          <SegmentChip active={activeSegment === 'never_active'} onClick={() => handleSegmentClick('never_active')}>
            Never Active <span className="count">{segments.never_active}</span>
          </SegmentChip>
        </div>
      )}

      {/* Filters bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Search by email, name, or username..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={(val) => { setStatusFilter(val); setPage(1); setActiveSegment(null); }}
              allowClear
              style={{ width: 150 }}
              options={[
                { label: 'Free', value: 'free' },
                { label: 'Professional', value: 'professional' },
                { label: 'Enterprise', value: 'enterprise' },
                { label: 'Free Trial', value: 'free_trial' },
              ]}
            />
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={(dates) => { setDateRange(dates as any); setPage(1); }}
            />
          </Col>
        </Row>
      </Card>

      {/* Bulk actions */}
      {selectedRowKeys.length > 0 && (
        <Card size="small" style={{ marginBottom: 16, background: '#fff7e6' }}>
          <Space>
            <span>{selectedRowKeys.length} users selected</span>
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} users?`}
              description="This will cascade-delete all associated data."
              onConfirm={handleBulkDelete}
              okText="Delete All"
              okType="danger"
            >
              <Button danger icon={<DeleteOutlined />}>Delete Selected</Button>
            </Popconfirm>
            <Button icon={<MailOutlined />} onClick={() => {
              navigate('/email', { state: { selectedUserIds: selectedRowKeys } });
            }}>
              Email Selected
            </Button>
          </Space>
        </Card>
      )}

      {/* Users table */}
      <Table
        dataSource={users}
        columns={columns}
        rowKey="_id"
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (total) => `${total} users`,
        }}
        onChange={handleTableChange}
        size="middle"
        scroll={{ x: 900 }}
      />
    </div>
  );
};

export default Users;
